import { EnvelopeIcon, QuestionMarkCircleIcon, UserCircleIcon } from "@heroicons/react/24/outline";
import { t } from "@lingui/core/macro";
import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { selectActiveOnboardingSessionId, selectCognitoId } from "@/redux/app/appSelectors";
import { useGetOnboardingSessionQuery } from "@/routes/_authenticated/onboarding/-api/onboarding";
import { Button } from "@/shared/components/ui/button";
import { CircularProgress } from "@/shared/components/ui/circular-progress";

import OnboardingFeedbackDialog from "./onboarding/-components/OnboardingFeedbackDialog";

function OnboardingNotFound() {
  return (
    <div className="absolute inset-0 m-auto flex flex-col items-center justify-center gap-2">
      <UserCircleIcon className="h-10 w-10 text-primary-light" />
      <p className="mx-4 max-w-lg text-center [textWrap:balance]">{t`No onboarding session found`}</p>
      <Button color="primary" render={<a href="mailto:accounts@monetary-metals.com" />}>
        <EnvelopeIcon className="mr-2 h-6 w-auto" />
        {t`Contact Support`}
      </Button>
    </div>
  );
}

function OnboardingLayout() {
  const pathname = useLocation().pathname;
  const navigate = useNavigate();
  const cognitoId = useSelector(selectCognitoId) as string;

  const sessionId = useSelector(selectActiveOnboardingSessionId);
  const { data, isUninitialized, isLoading } = useGetOnboardingSessionQuery(
    { cognitoId, sessionId },
    { skip: !cognitoId || !sessionId },
  );
  const posthog = usePostHog();

  useEffect(() => {
    if (!data) return;
    const { status } = data;
    posthog.register({
      onboarding_session_id: sessionId,
    });
    if (status === "complete" && pathname !== "/onboarding/success") {
      navigate({ to: "/onboarding/success", replace: true });
    }
    if (status === "signing" && pathname !== "/onboarding/docs") {
      navigate({ to: "/onboarding/docs", replace: true });
    }
    if (
      status === "forms" &&
      (pathname === "/onboarding" || pathname === "/onboarding/docs" || pathname === "/onboarding/success")
    ) {
      navigate({ to: "/onboarding/$step", params: { step: "accountType" }, replace: true });
    }
  }, [data, pathname, navigate, sessionId, posthog]);

  if (isUninitialized && !data) {
    return <OnboardingNotFound />;
  }

  if (isLoading) {
    return <CircularProgress className="absolute inset-0 m-auto" />;
  }

  return (
    <>
      <Outlet />

      {/* Floating Action Button for Feedback */}
      <div className="fixed right-6 bottom-6 z-50">
        <OnboardingFeedbackDialog>
          <Button className="rounded-full shadow-lg transition-shadow duration-200 hover:shadow-xl">
            <QuestionMarkCircleIcon className="mr-2 h-5 w-5" />
            {t`Need help?`}
          </Button>
        </OnboardingFeedbackDialog>
      </div>
    </>
  );
}

export const Route = createFileRoute("/_authenticated/onboarding")({
  component: OnboardingLayout,
  context: () => ({ permissions: "onboarding" }),
});
